import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { skipWhile } from 'rxjs/operators';

import { AnimalType, Product, ProductType } from '../../../shared/models';
import { setSelectedProductRequest } from '../../store/actions/product.actions';
import { getSelectedCountry, getSelectedProduct } from '../../store/selectors/product.selectors';

export interface ProductDetailDialogInputData extends AbstractFormDialogData {
  productId?: number;
}
export interface ProductDetailDialogResultData {
  product: Product;
}

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  standalone: false,
})
export class ProductDetailComponent
  extends AbstractFormDialog<ProductDetailDialogResultData>
  implements OnDestroy
{
  @Input() formGroup: FormGroup;

  pageTitle: string = this.locale.translate('create-product.title-create');
  selectedCountry$: Observable<string> = this.store.select(getSelectedCountry);

  product: Product;
  subProduct: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductDetailDialogInputData,
    protected mdDialogRef: MatDialogRef<
      ProductDetailComponent,
      AbstractDialogResultData<ProductDetailDialogResultData>
    >,
    private locale: LocaleService,
    private readonly store: Store<RootState>,
  ) {
    super();

    this.data.title = this.locale.translate('create-product.title-create');

    this.formGroup = new FormGroup({
      productName: new FormControl<string>(null, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      productCode: new FormControl<string>(null, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      type: new FormControl<ProductType>(null, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
    });

    const id = this.data.productId;
    if (id && id > 0) {
      this.store.dispatch(setSelectedProductRequest({ id }));
      this.subProduct = this.store
        .select(getSelectedProduct)
        .pipe(skipWhile((c) => c == null))
        .subscribe((product) => {
          this.product = product;
          this.pageTitle = `${this.locale.translate('create-product.title-edit')} ${product.name}`;
          this.formGroup.controls.productName.setValue(product.name);
          this.formGroup.controls.productCode.setValue(product.code);
          this.formGroup.controls.type.setValue(product.type);
        });
    }
  }

  ngOnDestroy(): void {
    if (this.subProduct) {
      this.subProduct.unsubscribe();
    }
  }

  save() {
    if (this.formGroup.valid) {
      this.selectedCountry$.subscribe((country) => {
        this.product = {
          ...this.product,
          name: <string>this.formGroup.controls.productName.value,
          code: <string>this.formGroup.controls.productCode.value,
          country,
          isActive: true,
          animalType: AnimalType.Layer,
          type: this.formGroup.controls.type.value,
        };
        this.confirm({
          product: this.product,
        });
      });
    }
  }
}
